import { extractFormilyFieldId } from "@sd/lib/parsePtsFromTicketRecord";

export type ChangeReqFormData = {
  requestedDate: string;
  expectedCompletion: string;
  assignedEmployee: string;
};

export const EMPTY_CHANGE_REQ_DATA: ChangeReqFormData = {
  requestedDate: "",
  expectedCompletion: "",
  assignedEmployee: "",
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function changeRequestParseSources(
  record: Record<string, unknown>
): Array<Record<string, unknown> | null> {
  const data = asRecord(record.data);
  const ticket = asRecord(record.ticket);
  const dataTicket = data ? asRecord(data.ticket) : null;
  return [
    record,
    data,
    ticket,
    dataTicket,
    findChangeRequestObject(record),
  ];
}

function copyFlatChangeRequestFields(
  merged: Record<string, unknown>,
  source: Record<string, unknown> | null
): void {
  if (!source) return;
  if (
    parseTruthyFlag(source.is_change_request ?? source.isChangeRequest) ||
    parseTruthyFlag(merged.is_change_request)
  ) {
    if (parseTruthyFlag(source.is_change_request ?? source.isChangeRequest)) {
      merged.is_change_request = true;
    }
  }
  for (const key of [
    "requested_date",
    "expected_completion_date",
    "assign_employees",
    "cr_number",
  ] as const) {
    if (isEmptyValue(merged[key]) && !isEmptyValue(source[key])) {
      merged[key] = source[key];
    }
  }
}

/** Nested `change_request` from ticket_create create/GET payload. */
export function pickChangeRequestObject(
  payload: Record<string, unknown>
): Record<string, unknown> | null {
  const direct = asRecord(payload.change_request);
  if (direct) return direct;
  const list = payload.change_request;
  if (Array.isArray(list) && list[0] && typeof list[0] === "object") {
    return list[0] as Record<string, unknown>;
  }
  return null;
}

function readChangeRequestId(payload: Record<string, unknown>): string {
  const nested = pickChangeRequestObject(payload);
  if (nested) {
    const nestedId = String(nested.id ?? nested.pk ?? nested.uuid ?? "").trim();
    if (nestedId) return nestedId;
  }
  for (const key of [
    "change_request_id",
    "changeRequestId",
    "cr_id",
    "change_request",
    "changeRequest",
  ]) {
    const id = extractFormilyFieldId(payload[key]);
    if (id) return id;
  }
  return "";
}

export function findChangeRequestObject(
  payload: unknown
): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;
  const data = asRecord(root.data);
  const ticket = asRecord(root.ticket);
  const dataTicket = data ? asRecord(data.ticket) : null;
  const nested =
    pickChangeRequestObject(root) ??
    (data ? pickChangeRequestObject(data) : null) ??
    (ticket ? pickChangeRequestObject(ticket) : null) ??
    (dataTicket ? pickChangeRequestObject(dataTicket) : null);
  if (nested) return nested;

  const id =
    readChangeRequestId(root) ||
    (data ? readChangeRequestId(data) : "") ||
    (ticket ? readChangeRequestId(ticket) : "") ||
    (dataTicket ? readChangeRequestId(dataTicket) : "");
  if (!id) return null;
  return { id };
}

function parseTruthyFlag(raw: unknown): boolean {
  if (raw === true || raw === 1) return true;
  if (typeof raw === "string") {
    const text = raw.trim().toLowerCase();
    return text === "true" || text === "1" || text === "yes";
  }
  return false;
}

function toDateInputValue(raw: unknown): string {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? "";
}

function parseAssignEmployeesField(raw: unknown): string {
  if (raw == null || raw === "") return "";
  if (Array.isArray(raw)) {
    return raw
      .map(item => {
        const id = extractFormilyFieldId(item);
        if (id) return id;
        const text = String(item ?? "").trim();
        return text && text !== "[object Object]" ? text : "";
      })
      .filter(Boolean)
      .join(",");
  }
  const text = String(raw).trim();
  if (!text) return "";
  if (text.startsWith("[")) {
    try {
      return parseAssignEmployeesField(JSON.parse(text) as unknown);
    } catch {
      return text;
    }
  }
  return text;
}

function firstDate(
  record: Record<string, unknown>,
  keys: readonly string[]
): string {
  for (const key of keys) {
    if (!(key in record)) continue;
    const value = toDateInputValue(record[key]);
    if (value) return value;
  }
  return "";
}

function firstAssignEmployees(
  ...records: Array<Record<string, unknown> | null>
): string {
  for (const record of records) {
    if (!record) continue;
    const value = parseAssignEmployeesField(
      record.assign_employees ??
        record.assignEmployees ??
        record.assigned_employees
    );
    if (value) return value;
  }
  return "";
}

/** Copy CR form fields onto the ticket record (does not touch ticket attachments). */
export function mergeChangeRequestIntoTicket(
  merged: Record<string, unknown>,
  root: Record<string, unknown>
): void {
  const changeRequest =
    findChangeRequestObject(root) ?? findChangeRequestObject(merged);

  copyFlatChangeRequestFields(merged, root);
  copyFlatChangeRequestFields(merged, asRecord(root.ticket));
  copyFlatChangeRequestFields(merged, asRecord(root.data));
  copyFlatChangeRequestFields(merged, asRecord(asRecord(root.data)?.ticket));
  copyFlatChangeRequestFields(merged, changeRequest);

  if (!changeRequest) return;

  merged.change_request = merged.change_request ?? changeRequest;

  merged.is_change_request = merged.is_change_request ?? true;
  for (const key of [
    "requested_date",
    "expected_completion_date",
    "assign_employees",
  ] as const) {
    if (isEmptyValue(merged[key]) && !isEmptyValue(changeRequest[key])) {
      merged[key] = changeRequest[key];
    }
  }
  if (!merged.change_request_id && changeRequest.id) {
    merged.change_request_id = changeRequest.id;
  }
  if (!merged.cr_number && changeRequest.cr_number) {
    merged.cr_number = changeRequest.cr_number;
  }
}

/** Read change-request fields from ticket_create GET/create payload for edit pre-fill. */
export function parseChangeRequestFromTicketRecord(
  record: Record<string, unknown>
): { includeChangeRequirement: boolean; changeReqData: ChangeReqFormData } {
  const sources = changeRequestParseSources(record);
  const nested = findChangeRequestObject(record);
  const crNumber = sources
    .map(src => String(src?.cr_number ?? src?.crNumber ?? "").trim())
    .find(Boolean) ?? "";
  const changeReqData: ChangeReqFormData = {
    requestedDate:
      sources
        .map(src =>
          src
            ? firstDate(src, [
                "requested_date",
                "requestedDate",
                "cr_requested_date",
              ])
            : ""
        )
        .find(Boolean) ?? "",
    expectedCompletion:
      sources
        .map(src =>
          src
            ? firstDate(src, [
                "expected_completion_date",
                "expected_completion",
                "expectedCompletionDate",
              ])
            : ""
        )
        .find(Boolean) ?? "",
    assignedEmployee: firstAssignEmployees(...sources),
  };

  const includeChangeRequirement =
    sources.some(src =>
      parseTruthyFlag(src?.is_change_request ?? src?.isChangeRequest)
    ) ||
    Boolean(nested?.id || nested?.cr_number || crNumber) ||
    Boolean(readChangeRequestId(record)) ||
    Boolean(
      changeReqData.requestedDate ||
        changeReqData.expectedCompletion ||
        changeReqData.assignedEmployee
    );

  return { includeChangeRequirement, changeReqData };
}

export type ChangeRequestSummary = {
  id: string;
  crNumber: string;
  requestedDate: string;
  expectedCompletion: string;
  assignedEmployeeIds: string[];
  status: string;
};

export function parseChangeRequestSummary(
  record: Record<string, unknown>
): ChangeRequestSummary | null {
  const nested = findChangeRequestObject(record);
  const parsed = parseChangeRequestFromTicketRecord(record);
  const crNumber = String(nested?.cr_number ?? record.cr_number ?? "").trim();
  const id = String(nested?.id ?? record.change_request_id ?? "").trim();
  if (
    !parsed.includeChangeRequirement &&
    !crNumber &&
    !id
  ) {
    return null;
  }
  return {
    id,
    crNumber,
    requestedDate: parsed.changeReqData.requestedDate,
    expectedCompletion: parsed.changeReqData.expectedCompletion,
    assignedEmployeeIds: parsed.changeReqData.assignedEmployee
      .split(",")
      .map(part => part.trim())
      .filter(Boolean),
    status: String(nested?.status ?? "").trim(),
  };
}

export function mergeParsedChangeRequest(
  ...parsed: Array<{
    includeChangeRequirement: boolean;
    changeReqData: ChangeReqFormData;
  } | null>
): { includeChangeRequirement: boolean; changeReqData: ChangeReqFormData } {
  const changeReqData = { ...EMPTY_CHANGE_REQ_DATA };
  let includeChangeRequirement = false;
  for (const item of parsed) {
    if (!item) continue;
    includeChangeRequirement =
      includeChangeRequirement || item.includeChangeRequirement;
    changeReqData.requestedDate =
      changeReqData.requestedDate || item.changeReqData.requestedDate;
    changeReqData.expectedCompletion =
      changeReqData.expectedCompletion || item.changeReqData.expectedCompletion;
    changeReqData.assignedEmployee =
      changeReqData.assignedEmployee || item.changeReqData.assignedEmployee;
  }
  return { includeChangeRequirement, changeReqData };
}

export function extractCreatedChangeRequestNumber(raw: unknown): string | null {
  const nested = findChangeRequestObject(raw);
  const number = String(nested?.cr_number ?? "").trim();
  return number || null;
}

export function appendChangeRequestFormFields(
  formData: FormData,
  args: {
    enabled: boolean;
    requestedDate: string;
    expectedCompletion: string;
    assignedEmployees: string;
  }
): void {
  formData.append("is_change_request", args.enabled ? "true" : "false");
  if (!args.enabled) return;
  formData.append("requested_date", args.requestedDate);
  formData.append("expected_completion_date", args.expectedCompletion);
  formData.append("assign_employees", args.assignedEmployees);
}
